import { EventType, ICreateRoomStateEvent, Preset } from 'matrix-js-sdk/src/matrix';
import { useCallback } from 'react';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import type { RoomAccessStateEntry } from 'hooks/roomCreation/useRoomAccessInitialState';
import type { RoomManagersInitialState } from 'hooks/roomCreation/useRoomManagersInitialState';
import { encryptionState } from 'hooks/useCreateMeeting/utils';
import useCreateRoom from 'hooks/useCreateRoom/useCreateRoom';
import useIsFeatureEnabled from 'hooks/useIsFeatureEnabled';
import { FeatureFlag } from 'utils/Constants';
import {
  buildCanonicalParentEvent,
  CustomMeedioMatrixEvents,
  getViaServers,
  inviteUsersToRoom,
  RoomPowerLevelOverridesOptions,
} from 'utils/matrixUtils';
import { logAndSendToSentry } from 'utils/utils';

export interface CreateMessengerRoomOptions {
  /** Room name (e.g. chat name, or "{user}'s chat" for video-hub group chats). */
  name?: string;
  /** Matrix IDs to invite after creation. Callers pre-validate as needed. */
  participantMxids: string[];
  /** Defaults to Preset.PrivateChat. Messenger passes PrivateChat/PublicChat from chat privacy. */
  preset?: Preset;
  workspaceId?: string | null;
  /** Workspace-scoped, caller-resolved (scope differs: 'chat' vs 'meeting'). Applied only when workspaceId is set. */
  roomManagersInitialState?: RoomManagersInitialState | null;
  roomAccessInitialState?: RoomAccessStateEntry | null;
  powerLevelOverrides?: RoomPowerLevelOverridesOptions;
  ownerPowerLevel?: number;
}

export interface CreateMessengerRoomResult {
  roomId: string;
  failedInvites: string[];
}

interface UseMessengerRoomCreationReturn {
  createMessengerRoom: (options: CreateMessengerRoomOptions) => Promise<CreateMessengerRoomResult>;
}

const useMessengerRoomCreation = (): UseMessengerRoomCreationReturn => {
  const isCrossSigningEnabled = useIsFeatureEnabled(FeatureFlag.FE_ENABLE_CROSS_SIGNING);
  const { matrixClient } = useMatrixContext();
  const { createRoom } = useCreateRoom();

  const createMessengerRoom = useCallback(
    async ({
      name,
      participantMxids,
      preset,
      workspaceId,
      roomManagersInitialState,
      roomAccessInitialState,
      powerLevelOverrides,
      ownerPowerLevel,
    }: CreateMessengerRoomOptions): Promise<CreateMessengerRoomResult> => {
      const customInitialStates: ICreateRoomStateEvent[] = [
        {
          type: CustomMeedioMatrixEvents.MESSENGER_ROOM,
          state_key: '',
          content: { is_messenger_room: true },
        },
        ...(isCrossSigningEnabled ? [encryptionState] : []),
        ...(workspaceId && roomManagersInitialState ? [roomManagersInitialState] : []),
        ...(workspaceId && roomAccessInitialState ? [roomAccessInitialState] : []),
        ...(workspaceId ? [buildCanonicalParentEvent(workspaceId, getViaServers(workspaceId, matrixClient))] : []),
      ];

      const room = await createRoom({
        title: name ?? '',
        preset: preset ?? Preset.PrivateChat,
        customInitialStates,
        includeBot: !!workspaceId,
        powerLevelOverrides: workspaceId ? powerLevelOverrides : undefined,
        ownerPowerLevel: workspaceId ? ownerPowerLevel : undefined,
      });

      const roomId = room?.room_id ?? '';
      if (!roomId) throw new Error('useMessengerRoomCreation: no room_id returned');

      if (workspaceId) {
        const viaServers = getViaServers(workspaceId, matrixClient);
        try {
          await matrixClient.sendStateEvent(workspaceId, EventType.SpaceChild, { via: viaServers }, roomId);
        } catch (error) {
          await matrixClient.leave(roomId).catch((leaveError) =>
            logAndSendToSentry('Failed to leave orphan messenger room after workspace link failure', {
              error: leaveError,
              roomId,
            })
          );
          throw error;
        }
      }

      const { failedInvites } = await inviteUsersToRoom(matrixClient, roomId, participantMxids);

      return { roomId, failedInvites };
    },
    [createRoom, matrixClient, isCrossSigningEnabled]
  );

  return { createMessengerRoom };
};

export default useMessengerRoomCreation;
